import { useState, useEffect, useCallback } from 'react';
import AuthService from '../../services/AuthService';
import AttendanceService from '../../services/AttendanceService';
import {
  ATTENDANCE_STATUSES,
  getAttendanceStatus, getApprovalStatus, getLeaveType,
  fmtMinutes, fmtTime, fmtDate,
} from '../../constants/attendanceConstants';

// ─── style tokens (same design language) ─────────────────────────────────────
const card = { background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,.08)', padding: 24 };
const badge = (color) => ({
  display: 'inline-block', padding: '3px 12px', borderRadius: 20,
  fontSize: 12, fontWeight: 700, color: '#fff', background: color,
});
const btn = (bg, color = '#fff') => ({
  padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
  background: bg, color, fontWeight: 700, fontSize: 13,
});
const initials = (name = '') =>
  (name || 'UN').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
const avatarColors = ['#5e72e4', '#2dce89', '#fb6340', '#11cdef', '#825ee4', '#f5365c'];
const avatarColor = (name) => avatarColors[(name || '').charCodeAt(0) % avatarColors.length];

/** Monday-start week (local calendar). */
function mondayISO(d = new Date()) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x.toISOString().slice(0, 10);
}

function addDaysISO(iso, n) {
  const x = new Date(`${iso}T12:00:00`);
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
}

export default function TeamAttendance() {
  const clientId = AuthService.getLoggedInClientId();
  const userId   = AuthService.getLoggedInUserId();

  const [dateFrom, setDateFrom] = useState(mondayISO());
  const [dateTo, setDateTo]     = useState(() => addDaysISO(mondayISO(), 6));
  const [team, setTeam]         = useState([]);
  const [leaves, setLeaves]     = useState([]);
  const [tab, setTab]           = useState('attendance'); // 'attendance' | 'leaves'
  const [toast, setToast]       = useState(null);
  const [markModal, setMarkModal] = useState(null); // { record }

  const showMsg = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadTeam = useCallback(async () => {
    if (!clientId || dateFrom > dateTo) return;
    try {
      const from = new Date(`${dateFrom}T00:00:00`);
      const to = new Date(`${dateTo}T23:59:59`);
      setTeam(await AttendanceService.getTeamAttendance(clientId, { from, to }));
    } catch { /* silent */ }
  }, [clientId, dateFrom, dateTo]);

  const loadLeaves = useCallback(async () => {
    try { setLeaves(await AttendanceService.getPendingLeaves(clientId)); } catch { /* silent */ }
  }, [clientId]);

  useEffect(() => { loadTeam(); }, [loadTeam]);
  useEffect(() => { loadLeaves(); }, [loadLeaves]);

  const handleLeaveAction = async (id, action, note = '') => {
    try {
      if (action === 'approve') await AttendanceService.approveLeave(id, userId, note);
      else await AttendanceService.rejectLeave(id, userId, note);
      showMsg(`Leave ${action}d successfully.`);
      loadLeaves();
    } catch { showMsg('Action failed.', 'error'); }
  };

  const handleMarkStatus = async (uid, status, workDateISO) => {
    try {
      const d = workDateISO ? new Date(workDateISO) : new Date(`${dateFrom}T12:00:00`);
      await AttendanceService.markLeave(uid, d, status, '');
      showMsg(`Marked as ${status}.`);
      setMarkModal(null);
      loadTeam();
    } catch { showMsg('Failed to mark.', 'error'); }
  };

  // ─── summary counts ───────────────────────────────────────────────────────
  const summary = ATTENDANCE_STATUSES.reduce((acc, s) => {
    acc[s.id] = team.filter((t) => t.status === s.id).length;
    return acc;
  }, {});
  const total = team.length;

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'error' ? '#f5365c' : '#2dce89',
          color: '#fff', padding: '14px 28px', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,.2)', fontWeight: 700, fontSize: 15,
        }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1a1f36' }}>
            <i className="fas fa-users" style={{ color: '#5e72e4', marginRight: 10 }} />Team Attendance
          </h3>
          <p style={{ margin: '4px 0 0', color: '#8898aa' }}>
            Filter by date range (defaults to the current week). Leave requests are unchanged.
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <label style={{ fontSize: 13, fontWeight: 700, color: '#344767', display: 'flex', alignItems: 'center', gap: 8 }}>
            From
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #e3e6ec', fontSize: 14, fontWeight: 600 }} />
          </label>
          <label style={{ fontSize: 13, fontWeight: 700, color: '#344767', display: 'flex', alignItems: 'center', gap: 8 }}>
            To
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #e3e6ec', fontSize: 14, fontWeight: 600 }} />
          </label>
          <button
            type="button"
            onClick={() => {
              const start = mondayISO();
              setDateFrom(start);
              setDateTo(addDaysISO(start, 6));
            }}
            style={{ ...btn('#edf2ff', '#5e72e4'), border: '1px solid #d7dff7' }}
          >
            <i className="fas fa-calendar-week" style={{ marginRight: 6 }} />
            This week
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 14, marginBottom: 24 }}>
        <div style={{ ...card, textAlign: 'center', background: 'linear-gradient(135deg,#5e72e4,#825ee4)', color: '#fff' }}>
          <p style={{ margin: 0, opacity: .8, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Total</p>
          <h2 style={{ margin: '6px 0 0', fontSize: 32 }}>{total}</h2>
        </div>
        {ATTENDANCE_STATUSES.map((s) => summary[s.id] > 0 && (
          <div key={s.id} style={{ ...card, textAlign: 'center', borderTop: `4px solid ${s.color}` }}>
            <i className={s.icon} style={{ color: s.color, fontSize: 22, marginBottom: 6 }} />
            <p style={{ margin: 0, color: '#8898aa', fontSize: 12, fontWeight: 700 }}>{s.label}</p>
            <h3 style={{ margin: '4px 0 0', color: '#1a1f36', fontSize: 26 }}>{summary[s.id]}</h3>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#fff', borderRadius: 12, padding: 5, width: 'fit-content', boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
        {[['attendance', 'fas fa-calendar-check', 'Attendance'], ['leaves', 'fas fa-umbrella-beach', `Leaves (${leaves.filter(l => l.approvalStatus === 'Pending').length} pending)`]].map(([key, icon, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
            background: tab === key ? '#5e72e4' : 'transparent',
            color: tab === key ? '#fff' : '#8898aa',
          }}>
            <i className={icon} style={{ marginRight: 7 }} />{label}
          </button>
        ))}
      </div>

      {/* ── ATTENDANCE TAB ── */}
      {tab === 'attendance' && (
        <div style={card}>
          {dateFrom > dateTo ? (
            <p style={{ color: '#f5365c', textAlign: 'center', margin: '24px 0' }}>
              &quot;From&quot; date must be on or before &quot;To&quot; date.
            </p>
          ) : team.length === 0 ? (
            <p style={{ color: '#adb5bd', textAlign: 'center', margin: '40px 0' }}>
              <i className="fas fa-users-slash" style={{ fontSize: 32, marginBottom: 10, display: 'block' }} />
              No attendance records for this range.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)' }}>
                    {['Date', 'Employee', 'Status', 'Check In', 'Check Out', 'Working Hrs', 'Location', 'Actions'].map((h) => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#fff', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {team.map((row, idx) => {
                    const st = getAttendanceStatus(row.status);
                    return (
                      <tr key={`${row.id}-${row.workDate || idx}`} style={{ background: idx % 2 === 0 ? '#fafbff' : '#fff', borderBottom: '1px solid #f0f2f5' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: '#344767', whiteSpace: 'nowrap' }}>
                          {fmtDate(row.workDate)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 36, height: 36, borderRadius: '50%',
                              background: avatarColor(row.employeeName),
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontWeight: 800, fontSize: 13, flexShrink: 0,
                            }}>{initials(row.employeeName)}</div>
                            <span style={{ fontWeight: 600, color: '#1a1f36' }}>{row.employeeName}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={badge(st.color)}>
                            <i className={st.icon} style={{ marginRight: 4 }} />{st.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: '#2dce89' }}>{fmtTime(row.checkIn)}</td>
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: '#f5365c' }}>{fmtTime(row.checkOut)}</td>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: row.workingMinutes >= 480 ? '#2dce89' : '#fb6340' }}>
                          {fmtMinutes(row.workingMinutes)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {row.locationCheckIn ? (
                            <a href={`https://maps.google.com/?q=${row.locationCheckIn}`} target="_blank" rel="noreferrer"
                              style={{ color: '#5e72e4', fontSize: 12 }}>
                              <i className="fas fa-map-pin" /> Map
                            </a>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <button onClick={() => setMarkModal(row)} style={btn('#f4f7fa', '#5e72e4')}>
                            <i className="fas fa-edit" /> Mark
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── LEAVES TAB ── */}
      {tab === 'leaves' && (
        <div style={card}>
          {leaves.length === 0 ? (
            <p style={{ color: '#adb5bd', textAlign: 'center', margin: '40px 0' }}>No leave requests found.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)' }}>
                    {['Employee', 'Type', 'From', 'To', 'Reason', 'Status', 'Actions'].map((h) => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#fff', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((l, idx) => {
                    const ap = getApprovalStatus(l.approvalStatus);
                    return (
                      <tr key={l.id} style={{ background: idx % 2 === 0 ? '#fafbff' : '#fff', borderBottom: '1px solid #f0f2f5' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 34, height: 34, borderRadius: '50%',
                              background: avatarColor(l.employeeName),
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontWeight: 800, fontSize: 12,
                            }}>{initials(l.employeeName)}</div>
                            <span style={{ fontWeight: 600 }}>{l.employeeName}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontWeight: 600 }}>{getLeaveType(l.leaveType)}</td>
                        <td style={{ padding: '12px 16px' }}>{fmtDate(l.startDate)}</td>
                        <td style={{ padding: '12px 16px' }}>{fmtDate(l.endDate)}</td>
                        <td style={{ padding: '12px 16px', color: '#8898aa', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.reason || '—'}</td>
                        <td style={{ padding: '12px 16px' }}><span style={badge(ap.color)}>{ap.label}</span></td>
                        <td style={{ padding: '12px 16px' }}>
                          {l.approvalStatus === 'Pending' && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => handleLeaveAction(l.id, 'approve')} style={btn('#2dce89')}>
                                <i className="fas fa-check" />
                              </button>
                              <button onClick={() => handleLeaveAction(l.id, 'reject')} style={btn('#f5365c')}>
                                <i className="fas fa-times" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Mark Status Modal */}
      {markModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ ...card, width: '100%', maxWidth: 380, position: 'relative' }}>
            <button onClick={() => setMarkModal(null)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#8898aa' }}>×</button>
            <h5 style={{ margin: '0 0 4px', color: '#1a1f36' }}>Mark Attendance</h5>
            <p style={{ margin: '0 0 18px', color: '#8898aa', fontSize: 13 }}>
              {markModal.employeeName} — {fmtDate(markModal.workDate)}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {ATTENDANCE_STATUSES.map((s) => (
                <button key={s.id} onClick={() => handleMarkStatus(markModal.userId, s.id, markModal.workDate)}
                  style={{
                    padding: '12px', borderRadius: 10, border: `2px solid ${s.color}`,
                    background: markModal.status === s.id ? s.color : `${s.color}15`,
                    color: markModal.status === s.id ? '#fff' : s.color,
                    fontWeight: 700, cursor: 'pointer', fontSize: 13,
                  }}>
                  <i className={s.icon} style={{ marginRight: 5 }} />{s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
